const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : `${window.location.origin}/api`;

class ApiService {
  constructor() {
    this.token = localStorage.getItem('nfe_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('nfe_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('nfe_token');
    localStorage.removeItem('nfe_user');
  }

  getUser() {
    const u = localStorage.getItem('nfe_user');
    return u ? JSON.parse(u) : null;
  }

  setUser(user) {
    localStorage.setItem('nfe_user', JSON.stringify(user));
  }

  async request(path, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_URL}${path}`, { ...options, headers });

    if (response.status === 401) {
      this.clearToken();
      window.location.reload();
      throw new Error('Sessão expirada');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ erro: 'Erro desconhecido' }));
      // Quando o backend devolve {erro: <id>, mensagem: <texto humano>}, prefere
      // mensagem. Mantém compatibilidade com endpoints antigos que só têm `erro`.
      // Inclui `motivo` (rejeição SEFAZ — ex: inutilização cStat != 102) antes do
      // fallback genérico, para não colapsar tudo num "Erro <status>" sem causa.
      const err = new Error(error.mensagem || error.erro || error.motivo || `Erro ${response.status}`);
      err.codigo = error.codigo || error.erro;
      err.motivo = error.motivo;
      // Preserva campos extras do payload (ex: campos_faltantes na validação do emitente)
      if (error.campos_faltantes) err.camposFaltantes = error.campos_faltantes;
      if (error.acao_sugerida) err.acaoSugerida = error.acao_sugerida;
      if (error.liberado_em) err.liberadoEm = error.liberado_em;
      // Payload completo (diagnóstico da rejeição, rejeição original do 656…).
      err.dados = error;
      err.status = response.status;
      throw err;
    }

    if (options.blob) return response.blob();
    return response.json();
  }

  // Auth
  async login(email, senha) {
    const data = await this.request('/auth/login', {
      method: 'POST', body: JSON.stringify({ email, senha }),
    });
    this.setToken(data.token);
    this.setUser(data.usuario);
    return data;
  }

  async registrarUsuario(data) {
    return this.request('/auth/registro', { method: 'POST', body: JSON.stringify(data) });
  }

  async listarUsuarios(empresaId = null) {
    const qs = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    return this.request(`/auth/usuarios${qs}`);
  }

  async atualizarUsuario(id, data) {
    return this.request(`/auth/usuarios/${id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    });
  }

  async alternarUsuarioAtivo(id, ativo) {
    return this.request(`/auth/usuarios/${id}/ativo`, {
      method: 'PATCH', body: JSON.stringify({ ativo }),
    });
  }

  async marcarChangelogLido(versao) {
    return this.request('/auth/changelog/lido', {
      method: 'POST', body: JSON.stringify({ versao }),
    });
  }

  // Empresas
  async listarEmpresas() { return this.request('/empresas'); }
  async obterEmpresa(id) { return this.request(`/empresas/${id}`); }
  async criarEmpresa(data) {
    return this.request('/empresas', { method: 'POST', body: JSON.stringify(data) });
  }
  async atualizarEmpresa(id, data) {
    return this.request(`/empresas/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async uploadCertificado(empresaId, file, senha) {
    const formData = new FormData();
    formData.append('certificado', file);
    formData.append('senha', senha);
    const resp = await fetch(`${API_URL}/empresas/${empresaId}/certificado`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}` },
      body: formData,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ erro: 'Erro no upload' }));
      throw new Error(err.erro || 'Erro no upload');
    }
    return resp.json();
  }

  // Logotipo da empresa (impresso no DANFE)
  async uploadLogo(empresaId, file) {
    const formData = new FormData();
    formData.append('arquivo', file);
    // Sem Content-Type manual: o browser precisa definir o boundary do multipart.
    const resp = await fetch(`${API_URL}/empresas/${empresaId}/logo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}` },
      body: formData,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ erro: `Erro ${resp.status} ao enviar o logotipo` }));
      throw new Error(err.erro || 'Erro ao enviar o logotipo');
    }
    return resp.json();
  }
  // Blob para preview via URL.createObjectURL — <img src> não manda o JWT.
  obterLogo(empresaId) {
    return this.request(`/empresas/${empresaId}/logo?t=${Date.now()}`, { blob: true });
  }
  async removerLogo(empresaId) {
    return this.request(`/empresas/${empresaId}/logo`, { method: 'DELETE' });
  }

  // Tabelas do IBPT (Lei 12.741/2012) — painel do escritório (admin).
  // Prévia e importação devolvem { status, dados }: a prévia com problema (422)
  // traz a lista de erros no corpo, que a tela mostra.
  async enviarTabelaIbpt(acao, uf, file, sha256 = null) {
    const formData = new FormData();
    formData.append('arquivo', file);
    formData.append('uf', uf);
    if (sha256) formData.append('sha256', sha256);
    const resp = await fetch(`${API_URL}/ibpt/${acao}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}` },
      body: formData,
    });
    const dados = await resp.json().catch(() => ({ erro: `Erro ${resp.status} ao enviar a tabela` }));
    return { status: resp.status, dados };
  }
  async listarTabelasIbpt() { return this.request('/ibpt/versoes'); }
  // Relatórios de conferência (F0h, só admin): JSON {colunas, linhas, total}.
  async relatorioConferencia(chave, empresaId) {
    const q = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    return this.request(`/conferencia/${chave}${q}`);
  }
  // Crédito do Simples (F0e, só admin): percentual por empresa e competência.
  async listarCreditoSn(empresaId) { return this.request(`/credito-sn?empresa_id=${encodeURIComponent(empresaId)}`); }
  async criarCreditoSn(dados) { return this.request('/credito-sn', { method: 'POST', body: JSON.stringify(dados) }); }
  async desativarCreditoSn(id, motivo) {
    return this.request(`/credito-sn/${id}/desativar`, { method: 'POST', body: JSON.stringify({ motivo }) });
  }
  // Alíquotas oficiais do IBS e da CBS (C2, só admin): nunca apaga; alterar = substituir.
  async listarAliquotasOficiais() { return this.request('/aliquotas-oficiais'); }
  async coberturaAliquotasOficiais() { return this.request('/aliquotas-oficiais/cobertura'); }
  async historicoAliquotasOficiais() { return this.request('/aliquotas-oficiais/historico'); }
  async criarAliquotaOficial(dados) { return this.request('/aliquotas-oficiais', { method: 'POST', body: JSON.stringify(dados) }); }
  async desativarAliquotaOficial(id, motivo) {
    return this.request(`/aliquotas-oficiais/${id}/desativar`, { method: 'POST', body: JSON.stringify({ motivo }) });
  }
  async substituirAliquotaOficial(id, dados) {
    return this.request(`/aliquotas-oficiais/${id}/substituir`, { method: 'POST', body: JSON.stringify(dados) });
  }
  // Simples 2027 (NT 2026.005, só admin): alíquota efetiva do IBS/CBS por mês; só desativa.
  async listarAliquotaEfetivaSn(empresaId) { return this.request(`/aliquota-efetiva-sn?empresa_id=${encodeURIComponent(empresaId)}`); }
  async criarAliquotaEfetivaSn(dados) { return this.request('/aliquota-efetiva-sn', { method: 'POST', body: JSON.stringify(dados) }); }
  async desativarAliquotaEfetivaSn(id, motivo) {
    return this.request(`/aliquota-efetiva-sn/${id}/desativar`, { method: 'POST', body: JSON.stringify({ motivo }) });
  }
  // Opção do Simples pelo regime regular do IBS/CBS (C1b, só admin): por semestre; só desativa.
  async listarOpcaoRegimeRegular(empresaId) { return this.request(`/opcao-regime-regular?empresa_id=${encodeURIComponent(empresaId)}`); }
  async criarOpcaoRegimeRegular(dados) { return this.request('/opcao-regime-regular', { method: 'POST', body: JSON.stringify(dados) }); }
  async desativarOpcaoRegimeRegular(id, motivo) {
    return this.request(`/opcao-regime-regular/${id}/desativar`, { method: 'POST', body: JSON.stringify({ motivo }) });
  }
  // Tabela do DIFAL (F0c, só admin): alíquota interna e FCP da UF de destino.
  async listarDifal() { return this.request('/difal'); }
  async criarLinhaDifal(dados) { return this.request('/difal', { method: 'POST', body: JSON.stringify(dados) }); }
  async desativarLinhaDifal(id, motivo) {
    return this.request(`/difal/${id}/desativar`, { method: 'POST', body: JSON.stringify({ motivo }) });
  }
  async desativarTabelaIbpt(id, motivo) {
    return this.request(`/ibpt/versoes/${id}/desativar`, { method: 'POST', body: JSON.stringify({ motivo }) });
  }

  // Clientes
  // CE1: países da tabela oficial (sem o Brasil).
  async listarPaises() { return this.request('/clientes/paises'); }
  async listarClientes(page = 1, busca = '', perPage = 50) {
    return this.request(`/clientes?page=${page}&per_page=${perPage}&busca=${busca}`);
  }
  async criarCliente(data) {
    return this.request('/clientes', { method: 'POST', body: JSON.stringify(data) });
  }
  async atualizarCliente(id, data) {
    return this.request(`/clientes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async obterCliente(id) {
    return this.request(`/clientes/${id}`);
  }
  async excluirCliente(id) {
    return this.request('/clientes/' + id, { method: 'DELETE' });
  }

  // Produtos
  async listarProdutos(page = 1, busca = '', perPage = 50, classificacao = '') {
    const filtro = classificacao ? `&classificacao=${classificacao}` : '';
    return this.request(`/produtos?page=${page}&per_page=${perPage}&busca=${busca}${filtro}`);
  }
  async obterProduto(id) {
    return this.request(`/produtos/${id}`);
  }
  async criarProduto(data) {
    return this.request('/produtos', { method: 'POST', body: JSON.stringify(data) });
  }
  async atualizarProduto(id, data) {
    return this.request(`/produtos/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // NF-e
  async listarNotas(page = 1, status = '', busca = '', inicio = '', fim = '') {
    let url = `/nfe?page=${page}`;
    if (status) url += `&status=${status}`;
    if (busca)  url += `&busca=${encodeURIComponent(busca)}`;
    if (inicio) url += `&inicio=${inicio}`;
    if (fim)    url += `&fim=${fim}`;
    return this.request(url);
  }
  async obterNota(id) { return this.request(`/nfe/${id}`); }
  // Relatar erro ao suporte (e-mail). Corpo: { nota_id?, comentario, mensagem_exibida, detalhe_tecnico, tela }.
  async relatarErro(dados) {
    return this.request('/suporte/relatar-erro', { method: 'POST', body: JSON.stringify(dados) });
  }
  async emitirNFe(data) {
    return this.request('/nfe/emitir', { method: 'POST', body: JSON.stringify(data) });
  }
  async cancelarNFe(id, justificativa) {
    return this.request(`/nfe/${id}/cancelar`, {
      method: 'POST', body: JSON.stringify({ justificativa }),
    });
  }
  async cartaCorrecao(id, correcao) {
    return this.request(`/nfe/${id}/cce`, {
      method: 'POST', body: JSON.stringify({ correcao }),
    });
  }
  async listarCCes(notaId) {
    return this.request(`/nfe/${notaId}/cces`);
  }
  downloadCCePdf(notaId, sequencia) {
    return this.request(`/nfe/${notaId}/cce/${sequencia}/pdf`, { blob: true });
  }
  async inutilizar(data) {
    return this.request('/nfe/inutilizar', { method: 'POST', body: JSON.stringify(data) });
  }
  async statusSefaz() { return this.request('/nfe/status-sefaz'); }

  downloadXml(notaId, tipo = 'protocolo') {
    return this.request(`/nfe/${notaId}/xml?tipo=${tipo}`, { blob: true });
  }
  downloadDanfe(notaId) {
    return this.request(`/nfe/${notaId}/danfe`, { blob: true });
  }

  // Notas legado — arquivo morto do sistema anterior. Somente leitura:
  // não existe criar/alterar/excluir do lado do servidor.
  // cancelada: undefined = todas | true = so canceladas | false = so validas
  async listarNotasLegado(cancelada) {
    const qs = cancelada === undefined || cancelada === null
      ? '' : `?cancelada=${cancelada ? 'true' : 'false'}`;
    return this.request(`/nfe-legado${qs}`);
  }
  downloadDanfeLegado(notaId) {
    return this.request(`/nfe-legado/${notaId}/danfe`, { blob: true });
  }
  downloadXmlLegado(notaId) {
    return this.request(`/nfe-legado/${notaId}/xml`, { blob: true });
  }

  // Dashboard
  async dashboard(empresaId) {
    let url = '/dashboard';
    if (empresaId) url += `?empresa_id=${empresaId}`;
    return this.request(url);
  }
  async dashboardAdmin() { return this.request('/dashboard/admin'); }
}

const api = new ApiService();
export default api;

// Consulta CNPJ na Receita Federal
export async function consultaCnpj(cnpj) {
  const resp = await fetch(`${API_URL}/empresas/consulta-cnpj/${cnpj.replace(/[^0-9A-Za-z]/g, '').toUpperCase()}`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('nfe_token')}` }
  });
  if (!resp.ok) {
    const err = await resp.json();
    throw new Error(err.erro || 'Erro ao consultar CNPJ');
  }
  return resp.json();
}


// Excluir nota rejeitada
ApiService.prototype.excluirNota = function(id) {
  return this.request('/nfe/' + id, { method: 'DELETE' });
};

// Próximo número NF-e
ApiService.prototype.proximoNumero = function() {
  return this.request("/nfe/proximo-numero");
};

// Plano de devolução de venda própria (tipo de operação, destinatário e CFOP
// por item). Só consulta — a aplicação no formulário é feita pela tela, e o
// backend revalida tudo no /emitir.
ApiService.prototype.planoDevolucao = function(itens) {
  return this.request('/nfe/devolucao/plano', {
    method: 'POST',
    body: JSON.stringify({ itens }),
  });
};

// Devolução (F0h): consumidor final copiado da nota original (null = perguntar).
ApiService.prototype.consumidorFinalDevolucao = function(itens) {
  return this.request('/nfe/devolucao/consumidor-final', {
    method: 'POST',
    body: JSON.stringify({ itens }),
  });
};

// Salvar rascunho
ApiService.prototype.salvarRascunho = function(data) {
  return this.request('/nfe/rascunho', { method: 'POST', body: JSON.stringify(data) });
};

// Preview DANFE
ApiService.prototype.previewDanfe = async function(data) {
  const res = await fetch(`${API_URL}/nfe/preview-danfe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const dados = await res.json().catch(() => ({}));
    const err = new Error(dados.mensagem || dados.erro || 'Erro ao gerar prévia');
    err.status = res.status;
    err.codigo = dados.codigo;
    err.acaoSugerida = dados.acao_sugerida;
    err.dados = dados;
    throw err;
  }
  return res.blob();
};

// Relatório PDF por período
ApiService.prototype.relatorioNotas = async function(inicio, fim) {
  const res = await fetch(`${API_URL}/nfe/relatorio?inicio=${inicio}&fim=${fim}`, {
    headers: { 'Authorization': `Bearer ${this.token}` },
  });
  if (!res.ok) { const err = await res.json(); throw new Error(err.erro || 'Erro'); }
  return res.blob();
};

// Exclusão lógica de produto (soft delete → ativo=false)
ApiService.prototype.excluirProduto = function(id) {
  return this.request(`/produtos/${id}`, { method: 'DELETE' });
};

// Busca autocomplete de NCM
ApiService.prototype.buscarNcm = function(q, limit = 10) {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return this.request(`/ncm/buscar?${params.toString()}`);
};

// Classificação IBS/CBS (cClassTrib) — S27b
ApiService.prototype.buscarCclasstrib = function(q, limit = 10) {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return this.request(`/rtc/cclasstrib/buscar?${params.toString()}`);
};
ApiService.prototype.pendenciasClassificacao = function() {
  return this.request('/rtc/pendencias-classificacao');
};

// Pendências para emitir — mesmo payload da emissão, sem efeito colateral.
ApiService.prototype.pendenciasEmissao = function(data) {
  return this.request('/nfe/pendencias', { method: 'POST', body: JSON.stringify(data) });
};
// Quadro "Pendências por empresa" (admin).
ApiService.prototype.pendenciasPorEmpresa = function() {
  return this.request('/nfe/pendencias/empresas');
};

// Importação de produtos via XML de NF-e
ApiService.prototype.importarProdutosXml = async function(file) {
  const formData = new FormData();
  formData.append('xml', file);
  const res = await fetch(`${API_URL}/produtos/importar-xml`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${this.token}` },
    body: formData,
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.erro || 'Erro ao processar XML'); }
  return res.json();
};

ApiService.prototype.importarProdutosConfirmar = function(itens) {
  return this.request('/produtos/importar-confirmar', {
    method: 'POST', body: JSON.stringify({ itens }),
  });
};

// Dados para reemissão de NF-e rejeitada
ApiService.prototype.dadosReemissao = function(notaId) {
  return this.request(`/nfe/${notaId}/dados-reemissao`);
};

// Dados para retomar edição de rascunho
ApiService.prototype.dadosRascunho = function(notaId) {
  return this.request(`/nfe/${notaId}/dados-rascunho`);
};

// Info pública do certificado (validade, dias restantes, vencido?)
ApiService.prototype.infoCertificado = function(empresaId) {
  return this.request(`/empresas/${empresaId}/certificado/info`);
};

// Auditoria de alterações em empresa (admin only)
ApiService.prototype.listarAuditoria = function(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return this.request(`/auditoria/empresas${qs ? '?' + qs : ''}`);
};

// Regularização: sincroniza status local com SEFAZ via consulta por chave
ApiService.prototype.regularizarNota = function(notaId) {
  return this.request(`/nfe/${notaId}/regularizar`, { method: 'POST' });
};

// Nota "Transmitida" (envio sem resposta processada): consulta o protocolo na SEFAZ
ApiService.prototype.verificarTransmissao = function(notaId) {
  return this.request(`/nfe/${notaId}/verificar-transmissao`, { method: 'POST' });
};

// Inutilização: regra da UF (AC/BA/SP sem pedido desde 01/11/2026) e registro interno
ApiService.prototype.regraInutilizacao = function() {
  return this.request('/nfe/inutilizacao/regra');
};
ApiService.prototype.listarNumeracaoNaoUtilizada = function() {
  return this.request('/nfe/numeracao-nao-utilizada');
};
ApiService.prototype.registrarNumeracaoNaoUtilizada = function(data) {
  return this.request('/nfe/numeracao-nao-utilizada', { method: 'POST', body: JSON.stringify(data) });
};

// Inutilizações
ApiService.prototype.listarInutilizacoes = function() {
  return this.request('/nfe/inutilizacoes');
};

ApiService.prototype.baixarXmlInutilizacao = function(inutId, tipo = 'envio') {
  return this.request(`/nfe/inutilizacoes/${inutId}/xml?tipo=${tipo}`, { blob: true });
};

// Streaming SSE para o assistente IA fiscal
ApiService.prototype.assistenteStream = async function(payload, onChunk) {
  const resp = await fetch(`${API_URL}/assistente/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
    },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    let detail = '';
    try { const j = await resp.json(); detail = j.mensagem || j.erro || ''; } catch (e) {}
    if (resp.status === 401) {
      this.clearToken();
      window.location.reload();
      throw new Error('Sessão expirada');
    }
    throw new Error(`Erro ${resp.status}${detail ? ': ' + detail : ''}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const linha = part.trim();
      if (!linha.startsWith('data:')) continue;
      const json = linha.slice(5).trim();
      if (!json) continue;
      try { onChunk(JSON.parse(json)); } catch (e) {}
    }
  }
};

// Download XMLs ZIP por período
ApiService.prototype.downloadXmlsPeriodo = async function(inicio, fim) {
  const res = await fetch(`${API_URL}/nfe/xmls-periodo?inicio=${inicio}&fim=${fim}`, {
    headers: { 'Authorization': `Bearer ${this.token}` },
  });
  if (!res.ok) { const err = await res.json(); throw new Error(err.erro || 'Erro'); }
  return res.blob();
};
